import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from '../../core/services/api.service';
import { PagedResult, PaginationParams } from '../../core/models/api.models';
import {
  IssueDto,
  IssueDetailDto,
  IssueCommentDto,
  IssueLabelDto,
  ProjectComponentDto,
  IssueAttachmentDto,
  IssueLinkDto,
  IssueDependenciesDto,
  IssueHistoryDto,
  IssueActivityDto,
  IssueSummaryDto,
  IssueWatcherDto,
  IssueChecklistItemDto,
  EpicProgressDto,
  CreateIssueRequest,
  UpdateIssueRequest,
  IssueListParams,
  AddIssueAttachmentRequest,
  CreateIssueLinkRequest,
  UpdateIssueLinkRequest,
  CreateSubtaskRequest,
  CloneIssueRequest,
  BulkUpdateIssuesRequest,
  BulkUpdateIssuesResult
} from './issue.models';

@Injectable({ providedIn: 'root' })
export class IssueApiService {
  private readonly api = inject(ApiService);
  private readonly base = 'v1/issues';

  list(params: IssueListParams = {}): Observable<PagedResult<IssueDto>> {
    return this.api.getPaged<IssueDto>(this.base, params as unknown as PaginationParams);
  }

  getById(id: string): Observable<IssueDetailDto> {
    return this.api.get<IssueDetailDto>(`${this.base}/${id}`);
  }

  getByKey(key: string): Observable<IssueDetailDto> {
    return this.api.get<IssueDetailDto>(`${this.base}/by-key/${encodeURIComponent(key)}`);
  }

  create(request: CreateIssueRequest): Observable<IssueDetailDto> {
    return this.api.post<IssueDetailDto>(this.base, request);
  }

  update(id: string, request: UpdateIssueRequest): Observable<IssueDetailDto> {
    return this.api.put<IssueDetailDto>(`${this.base}/${id}`, request);
  }

  delete(id: string): Observable<unknown> {
    return this.api.delete(`${this.base}/${id}`);
  }

  assign(id: string, assigneeId: string | null): Observable<IssueDetailDto> {
    return this.api.post<IssueDetailDto>(`${this.base}/${id}/assign`, { assigneeId });
  }

  transition(id: string, status: number): Observable<IssueDetailDto> {
    return this.api.post<IssueDetailDto>(`${this.base}/${id}/transition`, { status });
  }

  getComments(issueId: string): Observable<IssueCommentDto[]> {
    return this.api.get<IssueCommentDto[]>(`${this.base}/${issueId}/comments`);
  }

  addComment(issueId: string, body: string, parentCommentId?: string | null): Observable<IssueCommentDto> {
    return this.api.post<IssueCommentDto>(`${this.base}/${issueId}/comments`, {
      body,
      parentCommentId: parentCommentId || null
    });
  }

  deleteComment(issueId: string, commentId: string): Observable<unknown> {
    return this.api.delete(`${this.base}/${issueId}/comments/${commentId}`);
  }

  toggleCommentReaction(issueId: string, commentId: string, emoji: string): Observable<IssueCommentDto['reactions']> {
    return this.api.post(`${this.base}/${issueId}/comments/${commentId}/reactions`, { emoji });
  }

  listLabels(projectId: string): Observable<IssueLabelDto[]> {
    return this.api.get<IssueLabelDto[]>(`v1/projects/${projectId}/labels`);
  }

  createLabel(projectId: string, name: string, color?: string): Observable<IssueLabelDto> {
    return this.api.post<IssueLabelDto>(`v1/projects/${projectId}/labels`, { name, color });
  }

  deleteLabel(projectId: string, labelId: string): Observable<unknown> {
    return this.api.delete(`v1/projects/${projectId}/labels/${labelId}`);
  }

  listComponents(projectId: string): Observable<ProjectComponentDto[]> {
    return this.api.get<ProjectComponentDto[]>(`v1/projects/${projectId}/components`);
  }

  createComponent(projectId: string, name: string, description?: string): Observable<ProjectComponentDto> {
    return this.api.post<ProjectComponentDto>(`v1/projects/${projectId}/components`, { name, description });
  }

  deleteComponent(projectId: string, componentId: string): Observable<unknown> {
    return this.api.delete(`v1/projects/${projectId}/components/${componentId}`);
  }

  // Phase 3
  getAttachments(issueId: string): Observable<IssueAttachmentDto[]> {
    return this.api.get<IssueAttachmentDto[]>(`${this.base}/${issueId}/attachments`);
  }

  addAttachment(issueId: string, request: AddIssueAttachmentRequest): Observable<IssueAttachmentDto> {
    return this.api.post<IssueAttachmentDto>(`${this.base}/${issueId}/attachments`, request);
  }

  removeAttachment(issueId: string, attachmentId: string): Observable<unknown> {
    return this.api.delete(`${this.base}/${issueId}/attachments/${attachmentId}`);
  }

  getLinks(issueId: string): Observable<IssueLinkDto[]> {
    return this.api.get<IssueLinkDto[]>(`${this.base}/${issueId}/links`);
  }

  getDependencies(issueId: string): Observable<IssueDependenciesDto> {
    return this.api.get<IssueDependenciesDto>(`${this.base}/${issueId}/dependencies`);
  }

  addLink(issueId: string, request: CreateIssueLinkRequest): Observable<IssueLinkDto> {
    return this.api.post<IssueLinkDto>(`${this.base}/${issueId}/links`, request);
  }

  updateLink(issueId: string, linkId: string, request: UpdateIssueLinkRequest): Observable<IssueLinkDto> {
    return this.api.put<IssueLinkDto>(`${this.base}/${issueId}/links/${linkId}`, request);
  }

  removeLink(issueId: string, linkId: string): Observable<unknown> {
    return this.api.delete(`${this.base}/${issueId}/links/${linkId}`);
  }

  getHistory(issueId: string): Observable<IssueHistoryDto[]> {
    return this.api.get<IssueHistoryDto[]>(`${this.base}/${issueId}/history`);
  }

  getActivity(issueId: string): Observable<IssueActivityDto[]> {
    return this.api.get<IssueActivityDto[]>(`${this.base}/${issueId}/activity`);
  }

  getSubtasks(issueId: string): Observable<IssueSummaryDto[]> {
    return this.api.get<IssueSummaryDto[]>(`${this.base}/${issueId}/subtasks`);
  }

  createSubtask(parentIssueId: string, request: CreateSubtaskRequest): Observable<IssueDetailDto> {
    return this.api.post<IssueDetailDto>(`${this.base}/${parentIssueId}/subtasks`, request);
  }

  linkToEpic(issueId: string, epicId: string | null): Observable<IssueDetailDto> {
    return this.api.post<IssueDetailDto>(`${this.base}/${issueId}/epic`, { epicId });
  }

  getEpicChildren(epicId: string): Observable<IssueSummaryDto[]> {
    return this.api.get<IssueSummaryDto[]>(`${this.base}/${epicId}/epic-children`);
  }

  getEpicProgress(epicId: string): Observable<EpicProgressDto> {
    return this.api.get<EpicProgressDto>(`${this.base}/${epicId}/epic-progress`);
  }

  listEpics(projectId: string): Observable<EpicProgressDto[]> {
    return this.api.get<EpicProgressDto[]>(`${this.base}/epics`, { projectId });
  }

  archive(issueId: string, archive: boolean): Observable<IssueDetailDto> {
    return this.api.post<IssueDetailDto>(`${this.base}/${issueId}/archive`, { archive });
  }

  clone(issueId: string, request: Partial<CloneIssueRequest> = {}): Observable<IssueDetailDto> {
    return this.api.post<IssueDetailDto>(`${this.base}/${issueId}/clone`, request);
  }

  bulkUpdate(request: BulkUpdateIssuesRequest): Observable<BulkUpdateIssuesResult> {
    return this.api.post<BulkUpdateIssuesResult>(`${this.base}/bulk`, request);
  }

  merge(issueId: string, targetIssueId: string, softDeleteSource = true, copyOpenComments = false): Observable<IssueDetailDto> {
    return this.api.post<IssueDetailDto>(`${this.base}/${issueId}/merge`, {
      targetIssueId,
      softDeleteSource,
      copyOpenComments
    });
  }

  // Watchers
  watch(issueId: string): Observable<unknown> {
    return this.api.post(`${this.base}/${issueId}/watch`, {});
  }

  unwatch(issueId: string): Observable<unknown> {
    return this.api.delete(`${this.base}/${issueId}/watch`);
  }

  getWatchers(issueId: string): Observable<IssueWatcherDto[]> {
    return this.api.get<IssueWatcherDto[]>(`${this.base}/${issueId}/watchers`);
  }

  // Checklist
  getChecklist(issueId: string): Observable<IssueChecklistItemDto[]> {
    return this.api.get<IssueChecklistItemDto[]>(`${this.base}/${issueId}/checklist`);
  }

  addChecklistItem(issueId: string, text: string, sortOrder?: number): Observable<IssueChecklistItemDto> {
    return this.api.post<IssueChecklistItemDto>(`${this.base}/${issueId}/checklist`, { text, sortOrder });
  }

  toggleChecklistItem(issueId: string, itemId: string): Observable<IssueChecklistItemDto> {
    return this.api.post<IssueChecklistItemDto>(`${this.base}/${issueId}/checklist/${itemId}/toggle`, {});
  }

  deleteChecklistItem(issueId: string, itemId: string): Observable<unknown> {
    return this.api.delete(`${this.base}/${issueId}/checklist/${itemId}`);
  }
}
